import { useEffect, useRef, useState } from 'react';
import type { ConciergeMessagePart } from '../types/conciergeToolTypes';

/**
 * Subscribes to the long-lived support-events SSE channel for the given
 * conversation. The backend forwards `data-*` events from the SupportEventBus
 * (human replies, takeover state transitions, ticket-status updates) over
 * this channel so they reach the chat UI even when no per-turn LLM stream is
 * open.
 *
 * - Opens on mount; closes on unmount.
 * - The browser auto-reconnects on transient network failures.
 * - Returned `liveParts` array grows as events arrive. The parent merges this
 *   with `conversationHistory`-derived parts to feed `useLiveTakeoverState`.
 * - `onReconnect` is fired on the SECOND (and any subsequent) `data-events-
 *   connected` event for a given conversation. This is the signal that the
 *   browser's EventSource silently re-established the channel after a drop —
 *   any events delivered to the bus during the gap were missed by us, so the
 *   parent should refetch the conversation to recover them from Mastra.
 *
 * Verbose `[concierge-events]` console logs are always-on. They include
 * EventSource lifecycle (created, connected, error, reconnect, closed),
 * full event payloads, and `readyState` transitions. Volume is small (a
 * few lines per minute on an idle chat, one per real event) and the
 * payload dump is the fastest way to diagnose live-delivery gaps from a
 * customer's browser console.
 *
 * The synthetic `data-debug-ping` event (published via
 * `POST /concierge/support/conversation/:id/debug-publish`) is logged to
 * the console but NOT pushed to `liveParts` — it's a diagnostic, not a
 * chat message.
 */
const SUPPORT_EVENT_TYPES = [
  'data-events-connected',
  'data-live-takeover-state',
  'data-human-reply',
  'data-live-takeover-started',
  'data-live-takeover-ended',
  'data-takeover-team-changed',
  'data-ticket-status-updated',
  'data-user-message-mirrored',
  'data-user-message-delivery-failed',
  'data-debug-ping',
] as const;

type SupportEventType = (typeof SUPPORT_EVENT_TYPES)[number];

const DEFAULT_BASE_URL = '';

const READY_STATE_LABEL: Record<number, string> = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSED',
};

export function useConciergeSupportEvents(
  conversationId: string | null | undefined,
  apiBaseUrl?: string,
  onReconnect?: () => void,
): ConciergeMessagePart[] {
  const [liveParts, setLiveParts] = useState<ConciergeMessagePart[]>([]);

  // Hold `onReconnect` in a ref so we can call the latest closure without
  // re-creating the EventSource on every parent render.
  const onReconnectRef = useRef(onReconnect);
  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  useEffect(() => {
    if (!conversationId) {
      setLiveParts([]);
      console.info('[concierge-events] skip_open no_conversation_id');
      return;
    }

    const baseUrl = apiBaseUrl ?? DEFAULT_BASE_URL;
    const url = `${baseUrl}/api-rewards/concierge/support/conversation/${encodeURIComponent(
      conversationId,
    )}/events`;

    let es: EventSource | null = null;
    const openedAt = Date.now();
    let lastReadyState: number | undefined;
    let readyStatePoll: ReturnType<typeof setInterval> | null = null;
    try {
      es = new EventSource(url, { withCredentials: true });
      console.info('[concierge-events] event_source_created', {
        conversationId,
        url,
        readyState: READY_STATE_LABEL[es.readyState] ?? es.readyState,
      });
    } catch (err) {
      console.warn('[concierge-events] event_source_create_failed', { conversationId, url, err });
      return;
    }

    // ReadyState polling — EventSource doesn't fire events on every state
    // transition, so we poll every 1s and log changes. Cheap (one timer
    // tick per second) and gives us "the connection went CLOSED at 3pm"
    // signals in the console without having to instrument the parent.
    readyStatePoll = setInterval(() => {
      const rs = es?.readyState;
      if (rs !== undefined && rs !== lastReadyState) {
        const fromLabel = lastReadyState === undefined ? 'INIT' : READY_STATE_LABEL[lastReadyState] ?? lastReadyState;
        const toLabel = READY_STATE_LABEL[rs] ?? rs;
        console.info('[concierge-events] ready_state_changed', {
          conversationId,
          from: fromLabel,
          to: toLabel,
          uptimeMs: Date.now() - openedAt,
        });
        lastReadyState = rs;
      }
    }, 1000);

    es.onerror = (errEvent) => {
      console.warn('[concierge-events] error', {
        conversationId,
        readyState: es ? READY_STATE_LABEL[es.readyState] ?? es.readyState : 'NULL',
        uptimeMs: Date.now() - openedAt,
        // EventSource error events don't carry useful payload — readyState
        // is the only signal worth logging. CONNECTING after error means
        // browser is auto-reconnecting; CLOSED means it gave up.
        errEvent,
      });
    };

    // Counts `data-events-connected` events. The backend writes one
    // immediately on every (re)connect, so a count >= 2 means the browser
    // auto-reconnected after a drop.
    let connectCount = 0;

    const listeners: Array<{ type: string; fn: (msg: MessageEvent) => void }> = [];

    for (const type of SUPPORT_EVENT_TYPES) {
      const fn = (msg: MessageEvent) => {
        let data: unknown;
        try {
          data = msg.data ? JSON.parse(msg.data) : undefined;
        } catch (err) {
          console.warn('[concierge-events] event_parse_failed', { conversationId, type, raw: msg.data, err });
          return;
        }

        // Verbose log for every event received. Full payload dump is
        // intentional — keep until live delivery has been stable for a
        // release cycle, then reduce to type+timestamp if noise is real.
        console.info('[concierge-events] event_received', {
          conversationId,
          type,
          data,
          uptimeMs: Date.now() - openedAt,
        });

        if (type === 'data-debug-ping') {
          // Synthetic diagnostic event — don't pollute liveParts; the
          // console line above is the assertion.
          return;
        }

        setLiveParts((prev) => [...prev, { type, data } as unknown as ConciergeMessagePart]);

        if (type === 'data-events-connected') {
          connectCount += 1;
          console.info('[concierge-events] connected', { conversationId, connectCount });
          if (connectCount >= 2) {
            console.info('[concierge-events] reconnected firing_onReconnect', {
              conversationId,
              connectCount,
            });
            try {
              onReconnectRef.current?.();
            } catch (err) {
              console.warn('[concierge-events] onReconnect_handler_threw', { conversationId, err });
            }
          }
        }
      };
      es.addEventListener(type, fn as EventListener);
      listeners.push({ type, fn });
    }

    return () => {
      console.info('[concierge-events] closing', {
        conversationId,
        uptimeMs: Date.now() - openedAt,
      });
      if (readyStatePoll) clearInterval(readyStatePoll);
      for (const { type, fn } of listeners) {
        es?.removeEventListener(type, fn as EventListener);
      }
      es?.close();
    };
  }, [conversationId, apiBaseUrl]);

  return liveParts;
}

// Re-export so callers (or future debug surfaces) can reference the same
// allowlist used in the FE-side EventSource subscription.
export type { SupportEventType };
export { SUPPORT_EVENT_TYPES };
